import {
  HIGH_LEVEL_VALUES_TO_LABELS,
  METRIC_VALUES_TO_LABELS,
} from 'constants/metricValuesToLabels';
import {
  selectIsContextualizedMetricsFeatureFlagOn,
  selectIsMetricDefinitionsFeatureFlagOn,
  selectIsWebInAppReportingFeatureFlagOn,
} from 'core/userContext';
import { kebabCase } from 'lodash';
import { InAppTooltipBreakdown } from 'modules/campaignAnalytics/components/InAppTooltipBreakdown';
import { WEB_INAPP_BREAKDOWN_UNSUPPORTED_METRICS } from 'modules/campaignAnalytics/constants';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { formatNumberGivenValueType, metricSentenceCase } from 'modules/productAnalytics/utils';
import { addPositiveToast } from 'modules/toastContainer';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, SecondaryButton } from 'ui/button';
import { AnalyticsCard } from 'ui/card';
import { PERCENTAGE } from 'ui/card/constants';
import { ChevronDown16, Export16 } from 'ui/icons';
import { NoResult } from 'ui/illustrations';
import { Menu } from 'ui/menu';
import { body2, colors, heading4, smallShadow, spacing } from 'ui/theme';
import { downloadAsCsvFile } from 'utils/csv';
import { DATE_FORMAT_TRUNC_YEAR } from 'utils/date';
import {
  AggregateInAppToBreakdown,
  CITY_DATA,
  DEVICE_DATA,
  HighLevelMediumAgnosticMetrics,
  MediumInAppToBreakdown,
  MediumOptions,
  ToggletipForFilteredProxiedOpensMetrics,
} from '../constants';
import { useCampaignAndMetricFiltersContext, useTimeFiltersContext } from '../context';
import { useRestData } from '../hooks/useRestData';
import {
  selectAggregateDestinationsByBreakdownMetrics,
  selectCampaigns,
  selectMediumDestinationsByBreakdownMetrics,
} from '../selectors';
import { DestinationsObj, InAppDestination, RestDataIdentifier } from '../types';
import { getCampaignMediumsByFrequency, getCampaignTotalText, getMetricMedium } from '../utils';
import { ComparisonBarChart } from './ComparisonBarChart';
import { OverviewLoadingSkeleton } from './LoadingSkeletons/OverviewLoadingSkeleton';
import { MetricDefinitionWrapper } from './MetricDefinitionWrapper';
import { NoDataMessage } from './NoDataMessage';

const processTooltip = (
  key: string,
  results: any,
  inAppFF: boolean,
  messageMedium: string,
  destinations: any,
) => {
  if (
    inAppFF &&
    messageMedium === 'InApp' &&
    MediumInAppToBreakdown[key] &&
    results[MediumInAppToBreakdown[key]]
  ) {
    let total = 0;
    (destinations[key] || []).forEach((destination: any) => {
      total += destination?.value ? parseInt(destination?.value, 10) : 0;
    });
    if (Object.keys(destinations).length === 0 || !destinations[key]) {
      return null;
    }
    return (
      <div data-test={`in-app-${kebabCase(key)}-destination-breakdown`}>
        {(destinations[key] || []).map((destination: any, idx: number) => (
          <div
            key={`${key}-${destination.appName}`}
            data-test={`in-app-${kebabCase(key)}-destination-breakdown-row-${idx}`}
          >
            <InAppTooltipBreakdown destination={destination} total={total} />
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const StyledOverviewHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledOverviewTitle = styled.div`
  ${heading4}
  margin-bottom: ${spacing.quarter};
`;

const StyledOverviewSubtitle = styled.div`
  ${body2}
  color: ${colors.textSecondary};
  margin-left: ${spacing.eighth};
`;

const StyledOverviewCardContainer = styled.div`
  display: grid;
  padding-top: ${spacing.plusHalf};
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${spacing.plusHalf};
`;

export const StyledFilterPerformanceCard = styled.div`
  background: ${colors.white};
  ${smallShadow};
  border-radius: ${spacing.half};
  padding: ${spacing.plusHalf};
  position: relative;
  overflow: hidden;
`;

const CardTitle = styled.div`
  ${body2}
  color: ${colors.textSecondary};
  display: flex;
  align-items: center;
`;

type OverviewSectionProps = {
  showFilteredProxiedOpensTooltips?: boolean;
};

export const OverviewSection = ({
  showFilteredProxiedOpensTooltips = false,
}: OverviewSectionProps) => {
  const dispatch = useDispatch();
  const isWebInAppReportingFeatureFlagOn = useSelector(selectIsWebInAppReportingFeatureFlagOn);
  const mediumDestinations = useSelector(selectMediumDestinationsByBreakdownMetrics);
  const aggregateDestinations: any = useSelector(selectAggregateDestinationsByBreakdownMetrics);
  const metricDefFFOn = useSelector(selectIsMetricDefinitionsFeatureFlagOn);
  const contextualizedMetricsFFOn = useSelector(selectIsContextualizedMetricsFeatureFlagOn);
  const { selectedCrossMetrics } = useCampaignAndMetricFiltersContext();
  const { analyticsMetrics, analyticsMetricsValueTypes, highLevelMetricsValueTypes } =
    useMetricsContext();
  const {
    data: aggregateData,
    loading: aggregatesLoading,
    error: showErrorState,
  } = useRestData(RestDataIdentifier.OVERVIEW);
  const ValueTypes = { ...analyticsMetricsValueTypes, ...highLevelMetricsValueTypes };

  // Campaigns overview (breakdown by medium)
  const campaigns = useSelector(selectCampaigns) || [];
  const totalCampaigns = campaigns.length;
  const numCampaignsPerMedium = getCampaignMediumsByFrequency(campaigns);
  const subtitle = getCampaignTotalText(numCampaignsPerMedium, totalCampaigns);

  // Export
  const { dateParams } = useTimeFiltersContext();
  const { startTime, endTime } = dateParams;
  const start = startTime?.format(DATE_FORMAT_TRUNC_YEAR);
  const end = endTime?.format(DATE_FORMAT_TRUNC_YEAR);
  const fileName = `messaging_insights_cross_medium_metrics (${start}-${end})`;
  const inAppFileName = `messaging_insights_in_app_breakdown (${start}-${end})`;
  const selectedMediums = Object.keys(numCampaignsPerMedium).map((medium) => MediumOptions[medium]);
  const headerRow = [`Medium (${start}-${end})`];
  const metricData: Record<string, (string | number)[]> = {};

  // Format multiMedium metric data for csv export
  const overallMetrics = aggregateData?.overallMetrics || {};
  const sortedOverallMetricList =
    selectedCrossMetrics?.reduce((agg: string[], metric: string) => {
      if (overallMetrics[HIGH_LEVEL_VALUES_TO_LABELS[metric]] !== undefined) {
        return [...agg, HIGH_LEVEL_VALUES_TO_LABELS[metric]];
      }
      return agg;
    }, []) || [];

  sortedOverallMetricList.forEach((label) => {
    headerRow.push(label);
    selectedMediums.forEach((medium) => {
      const metricVal = overallMetrics?.[label]?.mediums[medium]?.value;
      const formattedMetricVal = formatNumberGivenValueType(ValueTypes[label], metricVal);
      metricData[medium] = metricData[medium]
        ? [...metricData[medium], formattedMetricVal]
        : [formattedMetricVal];
    });
  });

  // Format medium-specific data for csv export
  const mediumMetrics = aggregateData?.mediumMetrics || {};
  const sortedMediumMetricList =
    selectedCrossMetrics?.reduce((agg: string[], metric: string) => {
      if (mediumMetrics[METRIC_VALUES_TO_LABELS[metric]] !== undefined) {
        return [...agg, METRIC_VALUES_TO_LABELS[metric]];
      }
      return agg;
    }, []) || [];

  sortedMediumMetricList.forEach((label) => {
    if (label !== CITY_DATA && label !== DEVICE_DATA) {
      headerRow.push(label);
      const metricMedium = MediumOptions[getMetricMedium(label, analyticsMetrics)];
      const val = mediumMetrics?.[label]?.value;
      selectedMediums.forEach((medium) => {
        const formattedVal =
          medium === metricMedium ? formatNumberGivenValueType(ValueTypes[label], val) : '';
        metricData[medium] = metricData[medium]
          ? [...metricData[medium], formattedVal]
          : [formattedVal];
      });
    }
  });

  const rows = [
    headerRow,
    ...Object.entries(metricData).map(([medium, metricVals]) => [medium, ...metricVals]),
  ];

  const handleExportCsv = () => {
    downloadAsCsvFile({ rows, fileName });
    dispatch(addPositiveToast('Metrics successfully exported', { key: 'export-success' }));
  };
  const destinationObj: any = {};
  const total: any = {
    destination: 'Total',
  };

  if (isWebInAppReportingFeatureFlagOn) {
    sortedOverallMetricList.forEach((metricName) => {
      if (AggregateInAppToBreakdown[metricName]) {
        (
          aggregateData?.mediumMetrics?.[AggregateInAppToBreakdown[metricName]]
            ?.value as DestinationsObj
        )?.destinations?.forEach((destination) => {
          if (destinationObj[destination.appName]) {
            destinationObj[destination.appName] = {
              ...destinationObj[destination.appName],
              [metricName]: destination?.value || 'N/A',
            };
          } else {
            destinationObj[destination.appName] = {
              destination: destination.appName,
              [metricName]: destination?.value || 'N/A',
            };
          }
        });
        total[metricName] =
          aggregateData?.overallMetrics?.[metricName]?.mediums?.[MediumOptions.InApp]?.value;
      }
    });
    destinationObj.total = total;
  }

  const handleExportInAppCsv = () => {
    downloadAsCsvFile({ rows: Object.values(destinationObj), fileName: inAppFileName });
    dispatch(addPositiveToast('Metrics successfully exported', { key: 'export-success' }));
  };

  const overflowActions: Record<string, any> = {
    'handle-export-csv': () => handleExportCsv(),
    'export-inapp-events': () => handleExportInAppCsv(),
  };

  return (
    <div>
      {!aggregatesLoading && !showErrorState && (
        <div>
          <StyledOverviewHeader>
            <div>
              <StyledOverviewTitle>Overall metrics</StyledOverviewTitle>
              <StyledOverviewSubtitle>{subtitle}</StyledOverviewSubtitle>
            </div>

            {isWebInAppReportingFeatureFlagOn && selectedMediums.includes('In-App') ? (
              <Menu
                handleSelectionChange={(id: string) => id && overflowActions[id]()}
                options={[
                  { label: 'Export metrics', id: 'handle-export-csv' },
                  { label: 'Export in-app events by destination', id: 'export-inapp-events' },
                ]}
              >
                <SecondaryButton>
                  <Export16 />
                  <span>Export metrics</span> <ChevronDown16 />
                </SecondaryButton>
              </Menu>
            ) : (
              <SecondaryButton onClick={handleExportCsv}>
                <Export16 />
                <span>Export metrics</span>
              </SecondaryButton>
            )}
          </StyledOverviewHeader>
          <StyledOverviewCardContainer>
            {sortedOverallMetricList.reduce((agg: React.ReactNode[], metricName) => {
              const metric = aggregateData?.overallMetrics?.[metricName] || {};
              const numberType = highLevelMetricsValueTypes[metricName];
              const valueMap: Record<string, number> = {};
              const mediumList: string[] = [];
              const toggletipsPerMedium: Record<string, string> = {};
              Object.keys(metric?.mediums || {}).forEach((medium) => {
                const val = metric?.mediums?.[medium]?.value;
                mediumList.push(medium);
                valueMap[medium] = val;
              });
              let destinations: InAppDestination[] = [];
              if (isWebInAppReportingFeatureFlagOn) {
                destinations = aggregateDestinations[metricName];
              }
              mediumList.sort((a: string, b: string) =>
                a.toLowerCase() < b.toLowerCase() ? -1 : 1,
              );
              if (isWebInAppReportingFeatureFlagOn && mediumList.includes(MediumOptions.InApp)) {
                const tooltipContent = WEB_INAPP_BREAKDOWN_UNSUPPORTED_METRICS.find(
                  (met) => met.aggregateMetricDisplayName === metricName,
                )?.tooltipContent;
                if (tooltipContent) {
                  toggletipsPerMedium[MediumOptions.InApp] = tooltipContent;
                }
              }

              if (!HighLevelMediumAgnosticMetrics.has(metricName)) {
                return [
                  ...agg,
                  <StyledFilterPerformanceCard
                    key={metricName}
                    data-test={`performance-card-${kebabCase(metricName)}`}
                  >
                    <CardTitle data-test={`metric-card-title-${kebabCase(metricName)}`}>
                      <MetricDefinitionWrapper metricName={metricName}>
                        {metricSentenceCase(metricName)}
                      </MetricDefinitionWrapper>
                    </CardTitle>
                    <ComparisonBarChart
                      numberType={numberType}
                      denominator={metric?.value === 0 ? 1 : metric?.value} // prevent 0 as a denom
                      valuesObj={valueMap}
                      mediumList={mediumList}
                      metricName={metricName}
                      destinations={destinations}
                      numCampaignsPerMedium={numCampaignsPerMedium}
                      toggletipsPerMedium={toggletipsPerMedium}
                    />
                  </StyledFilterPerformanceCard>,
                ];
              }
              return agg;
            }, [])}
          </StyledOverviewCardContainer>
          <StyledOverviewCardContainer>
            {sortedOverallMetricList.reduce((agg: React.ReactNode[], metricName) => {
              const metric = aggregateData?.overallMetrics?.[metricName] || {};
              const numberType = highLevelMetricsValueTypes[metricName];
              const TitleWrapper = ({ children }: { children: React.ReactNode }) => (
                <MetricDefinitionWrapper metricName={metricName}>
                  {children}
                </MetricDefinitionWrapper>
              );
              if (HighLevelMediumAgnosticMetrics.has(metricName)) {
                return [
                  ...agg,
                  <AnalyticsCard
                    key={metricName}
                    title={metricSentenceCase(metricName)}
                    TitleWrapper={TitleWrapper}
                    value={(numberType === PERCENTAGE ? 100 : 1) * metric?.value}
                    valueType={numberType}
                    AnalyticsCardContainerComponent={StyledFilterPerformanceCard}
                    metricDefFFOn={metricDefFFOn && !contextualizedMetricsFFOn}
                  ></AnalyticsCard>,
                ];
              }
              return agg;
            }, [])}
            {sortedMediumMetricList.map((metricName) => {
              let helpText = showFilteredProxiedOpensTooltips
                ? ToggletipForFilteredProxiedOpensMetrics[metricName]
                : '';
              if (isWebInAppReportingFeatureFlagOn && !helpText) {
                helpText = WEB_INAPP_BREAKDOWN_UNSUPPORTED_METRICS.filter(
                  (met) => met.mediumMetricDisplayName === metricName,
                )?.[0]?.tooltipContent;
              }
              const valueType = analyticsMetricsValueTypes[metricName];
              const TitleWrapper = ({ children }: { children: React.ReactNode }) => (
                <MetricDefinitionWrapper metricName={metricName}>
                  {children}
                </MetricDefinitionWrapper>
              );
              return (
                <AnalyticsCard
                  key={metricName}
                  title={metricSentenceCase(metricName)}
                  TitleWrapper={TitleWrapper}
                  value={
                    (valueType === PERCENTAGE ? 100 : 1) *
                    (aggregateData?.mediumMetrics?.[metricName].value as number)
                  }
                  valueTooltipContent={processTooltip(
                    metricName,
                    aggregateData?.mediumMetrics,
                    isWebInAppReportingFeatureFlagOn,
                    'InApp',
                    mediumDestinations,
                  )}
                  valueType={valueType}
                  AnalyticsCardContainerComponent={StyledFilterPerformanceCard}
                  helpText={helpText}
                  metricDefFFOn={metricDefFFOn && !contextualizedMetricsFFOn}
                ></AnalyticsCard>
              );
            })}
          </StyledOverviewCardContainer>
        </div>
      )}
      {aggregatesLoading && !showErrorState && (
        <div>
          <StyledOverviewHeader>
            <div>
              <StyledOverviewTitle>Overall metrics</StyledOverviewTitle>
              <StyledOverviewSubtitle>{subtitle}</StyledOverviewSubtitle>
            </div>

            <Button isDisabled>
              <Export16 />
              Export metrics
            </Button>
          </StyledOverviewHeader>
          <OverviewLoadingSkeleton numOfCards={selectedCrossMetrics?.length} />
        </div>
      )}
      {showErrorState && (
        <NoDataMessage
          Icon={NoResult}
          title="Sorry we couldn't load this page"
          subtitle="Try refreshing the page. If this keeps happening, let us know."
        />
      )}
    </div>
  );
};
